import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import socket, { connectSocket } from "../../services/socket-io";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useAuth = () => {
	const history = useHistory();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});

	const handleLogout = useCallback(async (showToast = true) => {
		setIsAuth(false);
		setUser({});
		localStorage.removeItem("token");
		localStorage.removeItem("user");
		api.defaults.headers.Authorization = undefined;

		if (showToast) {
			sessionStorage.setItem("sessionExpiredMessage", "Tu sesión ha expirado. Por favor, inicia sesión de nuevo.");
		}
		
		history.push("/login");
	}, [history]);

	useEffect(() => {
		const requestInterceptor = api.interceptors.request.use(
			(config) => {
				const token = localStorage.getItem("token");
				if (token) {
					config.headers["Authorization"] = `Bearer ${token}`;
				}
				return config;
			},
			(error) => Promise.reject(error)
		);

		const responseInterceptor = api.interceptors.response.use(
			(response) => response,
			async (error) => {
				const originalRequest = error.config;
				const status = error?.response?.status;

				const isPublicRoute = originalRequest.url.includes("/link") || originalRequest.url.includes("/public/");

				if (status === 401 && originalRequest.url.indexOf("login") === -1 && !isPublicRoute) {
					sessionStorage.setItem("sessionExpiredMessage", "Tu sesión ha expirado. Por favor, inicia sesión de nuevo.");
					await handleLogout();
				}
				
				return Promise.reject(error);
			}
		);

		return () => {
			api.interceptors.request.eject(requestInterceptor);
			api.interceptors.response.eject(responseInterceptor);
		};
	}, [handleLogout]);

	useEffect(() => {
		const token = localStorage.getItem("token");
		(async () => {
			if (token) {
				try {
					const { data } = await api.post("/auth/refresh_token");
					localStorage.setItem("token", data.token);
					api.defaults.headers.Authorization = `Bearer ${data.token}`;
					setIsAuth(true);
					setUser(data.user);
					connectSocket(data.token);
				} catch (err) {
					console.error("Initial refresh token failed:", err);
				}
			}
			setLoading(false);
		})();
	}, []);

	useEffect(() => {
		if (!user?.id) {
		  return;
		}
		
		const onUserUpdate = data => {
		  if (data.action === "update" && data.user.id === user.id) {
			setUser(data.user);
		  }
		};
	
		const onCompanyChanged = async ({ companyId }) => {
		  try {
				setUser(prev => (prev ? { ...prev, companyId } : prev));

				const { data } = await api.post("/auth/refresh_token");

				if (data?.token) {
					localStorage.setItem("token", data.token);
					api.defaults.headers.Authorization = `Bearer ${data.token}`;
					if (data.user) {
						setUser(data.user);
					}
					connectSocket(data.token);
				}
		  } catch (err) {
			toastError(err);
		  } finally {
			window.location.reload();
		  }
		};
	
		socket.on("user", onUserUpdate);
		socket.on("companyChanged", onCompanyChanged);
	
		return () => {
		  socket.off("user", onUserUpdate);
		  socket.off("companyChanged", onCompanyChanged);
		};
	  }, [user?.id]);

	const handleLogin = async userData => {
		setLoading(true);
		try {
			const { data } = await api.post("/auth/login", userData);
			localStorage.setItem("token", data.token);
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			setUser(data.user);
			setIsAuth(true);

			connectSocket(data.token);
			console.log("socket conectado?", socket.connected);
			socket.on("connect", () =>
				console.log("socket CONNECT event, id:", socket.id)
			);
			socket.on("disconnect", reason =>
				console.log("socket DISCONNECT event:", reason)
			);
			socket.on("connect_error", err =>
				console.log("socket CONNECT_ERROR:", err.message)
			);

			toast.success(i18n.t("auth.toasts.success"));
			history.push("/tickets");
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	return { isAuth, user, loading, handleLogin, handleLogout };
};

export default useAuth;